import React, { useId } from "react"
import { StepperHeading } from "./stepper-heading"
import { StepperList } from "./stepper-list"
import { StepperItem } from "./stepper-item"
import { StepperItemTrigger } from "./stepper-item-trigger"
import { StepperItemContent } from "./stepper-item-content"
import { StepperFooter } from "./stepper-footer"
import { cn } from "../utils"
import { StepperContext } from "./stepper-context"

export type { StepperHeadingProps } from "./stepper-heading"
export type { StepperListProps } from "./stepper-list"
export type { StepperItemProps, StepperItemState } from "./stepper-item"
export type { StepperItemTriggerProps } from "./stepper-item-trigger"
export type { StepperItemContentProps } from "./stepper-item-content"
export type { StepperFooterProps } from "./stepper-footer"

// =============================================================================
// Props
// =============================================================================

export type StepperProps = {
  /** A Stepper.Header, Stepper.List, and an optional Stepper.Footer */
  children?: React.ReactNode
  /** Additional classes merged onto the card container */
  className?: string
}

// =============================================================================
// Root
// =============================================================================

const containerStyles =
  "w-full rounded-[14px] border border-gray-200 bg-white p-6"

/**
 * A vertical stepper for showing progress through a multi-step flow.
 * Renders a bordered card containing a title, a list of steps, and an
 * optional footer.
 *
 * Compose with `Stepper.Heading`, `Stepper.List` (containing
 * `Stepper.Item` elements, optionally with `Stepper.ItemTrigger` and
 * `Stepper.ItemContent` for expandable steps), and `Stepper.Footer`.
 *
 * @example
 * ```tsx
 * <Stepper>
 *   <Stepper.Heading>What to expect</Stepper.Heading>
 *   <Stepper.List>
 *     <Stepper.Item state="completed">You're in the queue</Stepper.Item>
 *     <Stepper.Item state="active">We're processing your subscriber enrichment</Stepper.Item>
 *     <Stepper.Item state="upcoming">You'll get an email when your data is ready</Stepper.Item>
 *   </Stepper.List>
 * </Stepper>
 * ```
 */
function StepperRoot({ children, className }: StepperProps) {
  const headingId = useId()

  return (
    <StepperContext.Provider value={{ headingId }}>
      <section
        data-slot="stepper"
        aria-labelledby={headingId}
        className={cn(containerStyles, className)}
      >
        {children}
      </section>
    </StepperContext.Provider>
  )
}

StepperRoot.displayName = "Stepper"

// =============================================================================
// Compound component
// =============================================================================

interface StepperComponent {
  (props: StepperProps): React.JSX.Element
  Heading: typeof StepperHeading
  List: typeof StepperList
  Item: typeof StepperItem
  ItemTrigger: typeof StepperItemTrigger
  ItemContent: typeof StepperItemContent
  Footer: typeof StepperFooter
}

export const Stepper: StepperComponent = Object.assign(StepperRoot, {
  Heading: StepperHeading,
  List: StepperList,
  Item: StepperItem,
  ItemTrigger: StepperItemTrigger,
  ItemContent: StepperItemContent,
  Footer: StepperFooter
} as const)
